import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import pluralize from 'pluralize';
import { Button, Select, Spin, Table } from 'antd';
import moment from 'moment';
import cx from 'classnames';
import queryString from 'query-string';
import { isNil } from 'lodash';

import { isCorporateTASelector } from 'redux/selectors/authenticationSelectors';
import RenewalTypeModal from 'features/assignment/RenewAssignment/RenewalTypeModal';
import {
  getAssignmentsEndingSoonDetail,
  getAssignmentsEndingSoonDetailExcel,
} from 'redux/reducers/homeDashboardSlice';
import {
  getAssignmentsTableColumns,
  getAssignmentTypeFilterLabel,
  getAssignmentTypeLabel,
} from 'utils/assignmentUtils';
import { getSelectedFilterValues } from 'utils/filterUtils';
import {
  AssignmentTypeFilterEnum,
  RenewStatusEnum,
  SummaryAssignmentDetailModel,
  SummaryAssignmentDetailResponseModel,
} from 'api/backofficeApiClient';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import { typeConversionToNumberArray } from 'utils/convertUtils';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './AssignmentEndingSoonDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';

const assignmentFilterTypes = [
  AssignmentTypeFilterEnum.All,
  AssignmentTypeFilterEnum.W2Assignment,
  AssignmentTypeFilterEnum.ICAssignment,
  AssignmentTypeFilterEnum.SCAssignment,
];

const columns = (
  onRenewAssignment: React.Dispatch<React.SetStateAction<string>>,
) => [
  {
    title: 'Worker',
    key: 'worker',
    render: (record: SummaryAssignmentDetailModel) => (
      <>
        <Link className="wrapped-text" to={`/worker/${record.workerKey}`}>
          {record.worker}
        </Link>
      </>
    ),
  },
  {
    title: 'Job title',
    key: 'jobTitle',
    render: (record: SummaryAssignmentDetailModel) => record.jobTitle,
  },
  {
    title: 'End date',
    key: 'endDate',
    render: (record: SummaryAssignmentDetailModel) => (
      <>
        <span className={cx('icon icon-calendar', styles.calendarIcon)} />
        {moment.utc(record.jobEndDate).format('MM/DD/YYYY')}
      </>
    ),
  },
  {
    title: 'Last working day',
    key: 'lastWorkingDay',
    render: (record: SummaryAssignmentDetailModel) =>
      !isNil(record.terminationDate) && (
        <>
          <span className={cx('icon icon-calendar', styles.calendarIcon)} />
          {moment.utc(record.terminationDate).format('MM/DD/YYYY')}
        </>
      ),
  },

  {
    title: 'Assignment',
    key: 'assignments',
    render: (record: SummaryAssignmentDetailModel) => (
      <Link to={`/assignment/${record.assignmentKey}`}>
        {record.assignmentKey}
      </Link>
    ),
  },
  {
    title: 'Assignment type',
    key: 'assignmentType',
    render: (record: SummaryAssignmentDetailModel) =>
      getAssignmentTypeLabel(record.assignmentType),
  },
  {
    title: 'Customer',
    key: 'customer',
    render: (record: SummaryAssignmentDetailModel) => (
      <Link className="wrapped-text" to={`/customer/${record.customerKey}`}>
        {record.customerName}
      </Link>
    ),
  },
  {
    title: 'Customer ID',
    key: 'customerId',
    render: (record: SummaryAssignmentDetailModel) => record.customerKey,
  },
  {
    title: '',
    key: 'actions',
    render: (record: SummaryAssignmentDetailModel) => {
      const { assignmentId, renewStatus } = record;
      const isActiveRenew =
        renewStatus === RenewStatusEnum.RenewRequest ||
        renewStatus === RenewStatusEnum.ParentRenewRequest;

      if (isActiveRenew || !assignmentId) return null;

      return (
        <Button type="link" onClick={() => onRenewAssignment(assignmentId)}>
          Renew assignment
        </Button>
      );
    },
  },
];

export default function AssignmentsEndingSoonDetailsPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { assignmentTypes } = queryString.parse(location.search);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [
    isAssignmentsEndingSoonDataLoading,
    setIsAssignmentsEndingSoonDataLoading,
  ] = useState(false);
  const [assignmentsEndingSoonData, setAssignmentsEndingSoonData] =
    useState<SummaryAssignmentDetailResponseModel>();
  const [selectedTypes, setSelectedTypes] = useState<
    AssignmentTypeFilterEnum[]
  >(typeConversionToNumberArray(assignmentTypes));
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const [renewalId, setRenewalId] = useState('');
  const tableColumns = useMemo(
    () => getAssignmentsTableColumns(columns(setRenewalId), isCorporateTA),
    [isCorporateTA],
  );

  useEffect(() => {
    const queryData = {
      assignmentTypes: selectedTypes,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [selectedTypes]);

  const handleDownloadReport = async () => {
    const result = await dispatch(
      getAssignmentsEndingSoonDetailExcel(selectedTypes),
    );

    if (getAssignmentsEndingSoonDetailExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const assignmentsCountMessage = pluralize(
    'assignment',
    assignmentsEndingSoonData?.totalCount,
    true,
  );

  const loadAssignmentsEndingSoonData = useCallback(async () => {
    setIsAssignmentsEndingSoonDataLoading(true);

    const result = await dispatch(
      getAssignmentsEndingSoonDetail({
        page: currentTablePage - 1,
        assignmentTypes: selectedTypes,
      }),
    );

    if (getAssignmentsEndingSoonDetail.fulfilled.match(result)) {
      setAssignmentsEndingSoonData(result.payload);
    } else {
      setAssignmentsEndingSoonData({
        totalCount: 0,
        details: [],
      });
    }

    setIsAssignmentsEndingSoonDataLoading(false);
  }, [dispatch, selectedTypes, currentTablePage]);

  const loadInitialData = useCallback(async () => {
    await loadAssignmentsEndingSoonData();

    setIsInitialDataLoaded(true);
  }, [loadAssignmentsEndingSoonData]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleAssignmentTypesSelection = (
    types: AssignmentTypeFilterEnum[],
  ) => {
    const selectedFilterTypes = getSelectedFilterValues(
      types,
      AssignmentTypeFilterEnum.All,
    );

    setSelectedTypes(selectedFilterTypes);
    setCurrentTablePage(1);
  };

  const handleCloseRenewalTypeModal = () => {
    setRenewalId('');
  };

  return (
    <>
      {renewalId && (
        <RenewalTypeModal
          assignmentId={renewalId}
          onSubmit={loadAssignmentsEndingSoonData}
          onClose={handleCloseRenewalTypeModal}
        />
      )}

      <div className="page-wrapper scroll-box">
        <div className="page-holder">
          <BasePageLabel label="Dashboard details" />

          <div className="page-box page-inner">
            <div className="page-inner-container">
              <BaseTableHeader
                enableSubTitle={isInitialDataLoaded}
                subTitle={assignmentsCountMessage}
                title="Assignments ending soon"
              >
                <div className={commonStyles.selectBox}>
                  <Select
                    mode="multiple"
                    showArrow
                    allowClear
                    showSearch={false}
                    placeholder="Select assignment type"
                    style={{ width: '100%' }}
                    onChange={handleAssignmentTypesSelection}
                    value={selectedTypes}
                  >
                    {assignmentFilterTypes.map((type) => (
                      <Select.Option key={`select-type-${type}`} value={type}>
                        {getAssignmentTypeFilterLabel(type)}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div>
                  <Button
                    className={commonStyles.saveBtn}
                    onClick={handleDownloadReport}
                    icon={<span className="icon icon-xls-file" />}
                  />
                </div>
              </BaseTableHeader>

              {!isInitialDataLoaded && <Spin />}
              {isInitialDataLoaded && (
                <Table
                  className={cx(styles.table, 'custom-table')}
                  pagination={{
                    total: assignmentsEndingSoonData?.totalCount,
                    pageSize: 12,
                    onChange: setCurrentTablePage,
                    current: currentTablePage,
                    showSizeChanger: false,
                  }}
                  columns={tableColumns}
                  dataSource={assignmentsEndingSoonData?.details}
                  rowKey={(record) => record?.assignmentId as string}
                  loading={isAssignmentsEndingSoonDataLoading}
                  locale={{
                    emptyText:
                      'You currently do not have any assignments that are ending soon.',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
